<template>
    <div
        class="e-radio-wrapper tw:inline-flex tw:items-center tw:gap-2"
        :class="{ 'e-radio-wrapper--with-label': hasLabel }"
    >
        <RadioButton
            v-bind="primeVueProps"
            :input-id="resolvedInputId"
            @update:modelValue="$emit('update:modelValue', $event)"
        />
        <label v-if="hasLabel" :for="resolvedInputId" class="e-radio-label tw:cursor-pointer tw:pr-2">
            <slot />
        </label>
    </div>
</template>

<script>
import RadioButton from 'primevue/radiobutton';
import { computed, getCurrentInstance, useAttrs, useSlots } from 'vue';
/**
 * ERadio - Abstraction wrapper around PrimeVue's RadioButton
 *
 * Usage:
 *   <e-radio v-model="selected" native-value="option1">
 *     Option 1
 *   </e-radio>
 *   <e-radio v-model="selected" native-value="option2">
 *     Option 2
 *   </e-radio>
 *
 *   <e-radio v-model="size" native-value="small">
 *     Small
 *   </e-radio>
 *   <e-radio v-model="size" native-value="medium">
 *     Medium
 *   </e-radio>
 *   <e-radio v-model="size" native-value="large">
 *     Large
 *   </e-radio>
 *
 *   <e-radio v-model="choice" native-value="yes" type="is-success">
 *     Yes
 *   </e-radio>
 *   <e-radio v-model="choice" native-value="no" type="is-danger">
 *     No
 *   </e-radio>
 *
 * All Buefy b-radio props, events, and slots are supported.
 */
export default {
    name: 'ERadio',
    components: { RadioButton },
    inheritAttrs: false,
    props: {
        inputId: {
            type: String,
            default: undefined,
        },
    },
    emits: ['update:modelValue'],
    setup(props) {
        const attrs = useAttrs();
        const slots = useSlots();
        const instance = getCurrentInstance();

        const hasLabel = computed(() => Boolean(slots.default));

        const resolvedInputId = computed(() => {
            if (props.inputId != null && String(props.inputId).trim() !== '') {
                return String(props.inputId);
            }

            return `e-radio-${instance.uid}`;
        });

        const primeVueProps = computed(() => {
            const { 'native-value': nativeValue, ...rest } = attrs;

            return {
                ...rest,
                value: nativeValue,
                pt: {
                    box: ({ context }) => ({
                        class: context.checked
                            ? 'tw:border-primary tw:bg-white tw:w-5 tw:h-5 tw:rounded-full tw:border-2'
                            : 'tw:border-black tw:bg-white tw:w-5 tw:h-5 tw:rounded-full tw:border-2',
                    }),
                    icon: ({ context }) => ({
                        class: context.checked ? 'tw:bg-primary tw:rounded-full' : '',
                    }),
                },
            };
        });

        return { hasLabel, primeVueProps, resolvedInputId };
    },
};
</script>
